import React, { useMemo, useState } from 'react';
import { Button, Input, Select, Space, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { formatJsonText, validateJsonText } from './shared';

const DEFAULT_MODEL_CONFIG = `{
  "temperature": 0.7,
  "max_tokens": 2000,
  "top_p": 1.0,
  "frequency_penalty": 0,
  "presence_penalty": 0
}`;

const DEFAULT_SYSTEM_PROMPT =
  '你是一个专业的设计评审专家，负责评审设计方案的质量和可行性。';

const DEFAULT_USER_PROMPT = `请评审以下设计方案：
标题：{{input.designDoc.title}}
内容：{{input.designDoc.content}}
评分条件：{{input.score}}`;

const MODEL_OPTIONS = [
  { label: 'GPT-4 (OpenAI官方)', value: 'gpt-4-openai' },
  { label: 'GPT-4o (OpenAI官方)', value: 'gpt-4o-openai' },
  { label: 'Claude 3.5 Sonnet', value: 'claude-3-5-sonnet' },
  { label: 'DeepSeek-R1', value: 'deepseek-r1' },
];

const FieldBlock: React.FC<{
  label: string;
  extra?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, extra, children }) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-4">
        <div className="text-xs font-medium text-gray-500">{label}</div>
        {extra}
      </div>
      <div>{children}</div>
    </div>
  );
};

interface LLMModelConfigProps {
  ontologyId: number;
  processNodeId: string;
}

interface LLMModelFormState {
  modelId: string;
  modelConfigText: string;
  systemPrompt: string;
  userPromptTemplate: string;
}

function buildStorageKey(ontologyId: number, processNodeId: string) {
  return `opmcv:process-definition:llm:${ontologyId}:${processNodeId}`;
}

const LLMModelConfig: React.FC<LLMModelConfigProps> = ({
  ontologyId,
  processNodeId,
}) => {
  const [modelId, setModelId] = useState(MODEL_OPTIONS[0].value);
  const [modelConfigText, setModelConfigText] = useState(DEFAULT_MODEL_CONFIG);
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_SYSTEM_PROMPT);
  const [userPromptTemplate, setUserPromptTemplate] =
    useState(DEFAULT_USER_PROMPT);
  const [saving, setSaving] = useState(false);

  const promptTip = useMemo(
    () => '使用 {{input.参数名}} 引用输入参数',
    []
  );

  const handleFormatModelConfig = () => {
    const validation = validateJsonText(modelConfigText);
    if (!validation.valid) {
      message.error(validation.message);
      return;
    }

    setModelConfigText(formatJsonText(modelConfigText));
    message.success('模型参数已格式化');
  };

  const handleValidateModelConfig = () => {
    const validation = validateJsonText(modelConfigText);
    if (validation.valid) {
      message.success(validation.message);
    } else {
      message.error(validation.message);
    }
  };

  const handleSave = async () => {
    const validation = validateJsonText(modelConfigText);
    if (!validation.valid) {
      message.error(validation.message);
      return;
    }

    setSaving(true);
    try {
      const storageKey = buildStorageKey(ontologyId, processNodeId);
      const nextState: LLMModelFormState = {
        modelId,
        modelConfigText: formatJsonText(modelConfigText),
        systemPrompt,
        userPromptTemplate,
      };
      window.localStorage.setItem(storageKey, JSON.stringify(nextState));
      setModelConfigText(nextState.modelConfigText);
      message.success('LLM 大模型配置保存成功');
    } finally {
      setSaving(false);
    }
  };

  React.useEffect(() => {
    const storageKey = buildStorageKey(ontologyId, processNodeId);
    const cachedValue = window.localStorage.getItem(storageKey);
    if (!cachedValue) return;

    try {
      const parsedValue = JSON.parse(cachedValue) as Partial<LLMModelFormState>;
      if (parsedValue.modelId) setModelId(parsedValue.modelId);
      if (parsedValue.modelConfigText) {
        setModelConfigText(parsedValue.modelConfigText);
      }
      if (parsedValue.systemPrompt) setSystemPrompt(parsedValue.systemPrompt);
      if (parsedValue.userPromptTemplate) {
        setUserPromptTemplate(parsedValue.userPromptTemplate);
      }
    } catch (error) {
      console.error('加载 LLM 配置缓存失败:', error);
    }
  }, [ontologyId, processNodeId]);

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-4">
        <div className="text-sm font-semibold text-gray-800">LLM大模型配置</div>

        <FieldBlock label="选择大模型连接">
          <Select value={modelId} onChange={setModelId} options={MODEL_OPTIONS} />
        </FieldBlock>

        <FieldBlock
          label="模型参数配置（JSON）"
          extra={
            <Space size={8}>
              <Button type="link" size="small" onClick={handleFormatModelConfig}>
                格式化
              </Button>
              <Button type="link" size="small" onClick={handleValidateModelConfig}>
                校验
              </Button>
            </Space>
          }
        >
          <Input.TextArea
            rows={8}
            value={modelConfigText}
            onChange={event => setModelConfigText(event.target.value)}
          />
        </FieldBlock>

        <FieldBlock label="系统提示词 (System Prompt)">
          <Input.TextArea
            rows={4}
            value={systemPrompt}
            onChange={event => setSystemPrompt(event.target.value)}
          />
        </FieldBlock>

        <FieldBlock label="用户提示词模板">
          <div className="mb-2 text-xs text-gray-400">{promptTip}</div>
          <Input.TextArea
            rows={6}
            value={userPromptTemplate}
            onChange={event => setUserPromptTemplate(event.target.value)}
          />
        </FieldBlock>
      </section>

      <div className="flex items-center justify-end">
        <Button
          type="primary"
          icon={<SaveOutlined />}
          loading={saving}
          onClick={handleSave}
        >
          保存 LLM 大模型配置
        </Button>
      </div>
    </div>
  );
};

export default LLMModelConfig;
